import React, { useState, useEffect } from 'react';
import { questions } from '../../config/questions';

// Import the original question components
import SexQuestion from './SexQuestion';
import TreatmentQuestion from './TreatmentQuestion';
import DurationQuestion from './DurationQuestion';
import PaymentQuestion from './PaymentQuestion';
import StateQuestion from './StateQuestion';
import MenstrualCycleQuestion from './MenstrualCycleQuestion';
import PersonalInfoQuestion from './PersonalInfoQuestion';
import ContactInfoQuestion from './ContactInfoQuestion';
import AddressQuestion from './AddressQuestion';
import PhotoUploadQuestion from './PhotoUploadQuestion';
import TextQuestion from './TextQuestion';
import ReferralQuestion from './ReferralQuestion';
import ContraceptivesQuestion from './ContraceptivesQuestion';
import OtherMedsQuestion from './OtherMedsQuestion';
import TriedQuestion from './TriedQuestion';
import PrescriptionsQuestion from './PrescriptionsQuestion';
import MaternityQuestion from './MaternityQuestion';
import HasAllergiesQuestion from './HasAllergiesQuestion';

const QuestionEditComponent = ({
  questionType,
  currentAnswer,
  onSave,
  onCancel,
  selectedState, // For payment question dependency
  treatmentAnswer, // For prescriptions question dependency
  answers, // For photo upload question to get caseID and patientID
  questions // For photo upload question to get caseID and patientID
}) => {
  const [editAnswer, setEditAnswer] = useState(currentAnswer);

  // Reset edit answer when current answer changes
  useEffect(() => {
    setEditAnswer(currentAnswer);
  }, [currentAnswer]);

  const handleSave = (newAnswer) => {
    onSave(newAnswer);
  };

  const handleCancel = () => {
    setEditAnswer(currentAnswer);
    onCancel();
  };

  // In edit mode: only onSubmit persists, onNext is a NO-OP everywhere.
  const commonProps = {
    onSubmit: handleSave,
    onNext: () => { }, // No navigation in edit mode
    initialValue: editAnswer,
  };

  // Create modified versions of the question components that work in edit mode
  const renderEditComponent = () => {
    switch (questionType) {
      case 'personal-info':
        return <PersonalInfoQuestion {...commonProps} />;

      case 'contact-info':
        return <ContactInfoQuestion {...commonProps} />;

      case 'sex':
        return <SexQuestion {...commonProps} />;

      case 'treatment':
        return <TreatmentQuestion {...commonProps} />;

      case 'duration':
        return <DurationQuestion {...commonProps} />;

      case 'state':
        return <StateQuestion {...commonProps} isEditing={true} />;

      case 'payment':
        return <PaymentQuestion {...commonProps} selectedState={selectedState} />;

      case 'menstrual-cycle':
        return <MenstrualCycleQuestion {...commonProps} />;

      case 'address':
        return <AddressQuestion {...commonProps} />;

      case 'photo-upload':
        return <PhotoUploadQuestion {...commonProps} answers={answers} questions={questions} />;

      case 'referral':
        const referralQuestion = questions.find(q => q.id === 'referral');
        return <ReferralQuestion {...commonProps} question={referralQuestion} currentAnswer={editAnswer} />;

      case 'contraceptives':
        return <ContraceptivesQuestion {...commonProps} />;

      case 'other-meds':
        return <OtherMedsQuestion {...commonProps} />;

      case 'tried':
        return <TriedQuestion {...commonProps} />;

      case 'prescriptions':
        return <PrescriptionsQuestion {...commonProps} treatmentAnswer={treatmentAnswer} />;

      case 'maternity': {
        const showBirthControl = false;
        return <MaternityQuestion {...commonProps} showBirthControl={showBirthControl} />;
      }

      case 'has-allergies':
        return <HasAllergiesQuestion {...commonProps} />;

      case 'text':
      case 'allergies':
      case 'additional':
        return <TextQuestion {...commonProps} placeholder="Type your answer..." />;

      default:
        return <TextQuestion {...commonProps} placeholder="Type your answer..." />;
    }
  };

  const formRef = React.useRef(null);
  const [isUploading, setIsUploading] = React.useState(false);

  // Check if photo upload is in progress by checking the form's submit button state
  React.useEffect(() => {
    if (questionType !== 'photo-upload') return;

    const checkUploadState = () => {
      if (formRef.current) {
        const form = formRef.current.querySelector('form');
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton) {
            const uploading = submitButton.disabled && submitButton.textContent.includes('Uploading');
            setIsUploading(uploading);
          }
        }
      }
    };

    // Check periodically when editing photo upload
    const interval = setInterval(checkUploadState, 100);
    return () => clearInterval(interval);
  }, [questionType]);

  const handleSaveClick = () => {
    // Don't allow save if uploading
    if (isUploading) return;

    // Find the form in the rendered component and submit it
    if (formRef.current) {
      const form = formRef.current.querySelector('form');
      if (form) {
        // Check if the form's submit button is disabled (e.g., during upload)
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton && submitButton.disabled) {
          return; // Don't submit if the form's submit button is disabled
        }

        // Create a submit event and dispatch it
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    }
  };

  return (
    <div className="edit-component-container">
      <div className="edit-component-content" ref={formRef}>
        {renderEditComponent()}
      </div>

      <div className="edit-component-actions">
        <button className="cancel-button" onClick={handleCancel} disabled={isUploading}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSaveClick} disabled={isUploading}>
          {isUploading ? 'Uploading...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default QuestionEditComponent;
